export interface ZhinengwuzangParsed {
  mainConstitution: string;
  secondaryConstitution: string;
  tendency: string;
  mainSyndrome: string;
  accompanyingSyndrome: string;
  hint: string;
  chartCount: number;
  scores: number[];
  organScores: number[];
  evilScores: number[];
  yangScore: number;
  yinScore: number;
  constitutionText: string;
  syndromeText: string;
}

export const ORGAN_LABELS = ["肝", "心", "脾", "肺", "肾"];
export const EVIL_LABELS = ["痰湿", "淤血", "气滞", "内热", "内寒"];
const YANG_YIN_MAX = 72.5;
const ORGAN_CHART_MAX = 15;
const EVIL_CENTER = 5;

export function parseZhinengwuzangResult(
  raw: string
): ZhinengwuzangParsed | null {
  if (!raw) {
    return null;
  }
  const parts = raw.split("|");
  if (parts.length < 4) {
    return null;
  }

  const constitutionParts = parts[0].split("_");
  const syndromeParts = parts[1].split("_");
  const hint = parts[2] || "";
  const chartCount = parseInt(parts[3], 10) || 0;
  const scoreParts = parts[4] ? parts[4].split("_") : [];
  const scores = scoreParts.map((item) => parseFloat(item) || 0);

  const mainConstitution = constitutionParts[0] || "";
  const secondaryConstitution = constitutionParts[1] || "";
  const tendency = constitutionParts[2] || "";
  const mainSyndrome = syndromeParts[0] || "";
  const accompanyingSyndrome = syndromeParts[1] || "";

  const organScores = scores.slice(0, 5);
  const evilScores = scores.slice(9, 14);
  const qiDeficiency = scores[5] || 0;
  const bloodDeficiency = scores[6] || 0;
  const yinDeficiency = scores[7] || 0;
  const yangDeficiency = scores[8] || 0;
  const yangScore = YANG_YIN_MAX - qiDeficiency - yangDeficiency;
  const yinScore = YANG_YIN_MAX - bloodDeficiency - yinDeficiency;

  return {
    mainConstitution,
    secondaryConstitution,
    tendency,
    mainSyndrome,
    accompanyingSyndrome,
    hint,
    chartCount,
    scores,
    organScores,
    evilScores,
    yangScore,
    yinScore,
    constitutionText: formatConstitutionText(
      mainConstitution,
      secondaryConstitution,
      tendency
    ),
    syndromeText: formatSyndromeText(mainSyndrome, accompanyingSyndrome),
  };
}

function formatConstitutionText(
  main: string,
  secondary: string,
  tendency: string
): string {
  if (!main) {
    return "";
  }
  const parts = [main];
  if (secondary) {
    parts.push(secondary);
  }
  if (tendency) {
    parts.push(`${tendency}(倾向)`);
  }
  return parts.join("兼");
}

function formatSyndromeText(main: string, accompanying: string): string {
  if (!main && !accompanying) {
    return "无";
  }
  if (main && accompanying) {
    return `${main}、${accompanying}`;
  }
  return main || accompanying;
}

export function getEvilBarOption(scores: number[]) {
  const formatLabel = (value: number) => value.toFixed(1);

  const getBarColor = (total: number) => {
    if (total <= EVIL_CENTER) {
      return "#0a97a2";
    }
    const greenStop = EVIL_CENTER / total;
    return {
      type: "linear",
      x: 0,
      y: 1,
      x2: 0,
      y2: 0,
      colorStops: [
        { offset: 0, color: "#0a97a2" },
        { offset: greenStop, color: "#0a97a2" },
        { offset: greenStop, color: "#999999" },
        { offset: 1, color: "#999999" },
      ],
    };
  };

  return {
    color: ["#0a97a2"],
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      formatter: (params: any) => {
        const index = params[0].dataIndex;
        const total = scores[index];
        return `${params[0].name}<br/>分值：${formatLabel(total)}`;
      },
    },
    grid: {
      left: "50",
      right: "20",
      top: "40",
      bottom: "30",
    },
    xAxis: {
      type: "category",
      data: EVIL_LABELS,
      axisLabel: {
        color: "rgba(0,0,0,0.8)",
      },
      axisLine: {
        lineStyle: {
          color: "rgba(0,0,0,0.3)",
        },
      },
    },
    yAxis: {
      type: "value",
      max: 10,
      axisLine: {
        show: true,
      },
      splitLine: {
        lineStyle: {
          type: "dashed",
        },
      },
    },
    series: [
      {
        name: "实邪",
        type: "bar",
        barWidth: 40,
        data: scores.map((value, index) => ({
          value,
          itemStyle: {
            color: getBarColor(value),
          },
        })),
        emphasis: {
          itemStyle: {
            opacity: 0.85,
          },
        },
        label: {
          show: true,
          position: "top",
          distance: 4,
          color: "rgba(0,0,0,0.85)",
          formatter: (params: any) => formatLabel(scores[params.dataIndex]),
        },
        markLine: {
          silent: true,
          symbol: "none",
          lineStyle: {
            color: "#f56c6c",
            type: "dashed",
          },
          data: [{ yAxis: EVIL_CENTER }],
        },
      },
    ],
  };
}

export function getOrganRadarOption(scores: number[]) {
  const radarData = scores.map((score) =>
    Math.max(0, ORGAN_CHART_MAX - score)
  );

  return {
    color: ["#409eff"],
    radar: {
      center: ["50%", "52%"],
      radius: "62%",
      startAngle: 90,
      indicator: ORGAN_LABELS.map((name) => ({
        name,
        max: ORGAN_CHART_MAX,
      })),
      axisName: {
        color: "rgba(0,0,0,0.85)",
        fontSize: 15,
        fontWeight: 500,
      },
      axisLine: {
        lineStyle: {
          color: "rgba(0,0,0,0.2)",
        },
      },
      splitNumber: 3,
      splitLine: {
        lineStyle: {
          color: "#d9d9d9",
          width: 1,
        },
      },
      splitArea: {
        show: false,
      },
    },
    series: [
      {
        type: "radar",
        symbol: "circle",
        symbolSize: 5,
        data: [
          {
            name: "参考值10",
            value: ORGAN_LABELS.map(() => 10),
            lineStyle: {
              color: "#f56c6c",
              width: 1,
            },
            symbol: "none",
            areaStyle: {
              opacity: 0,
            },
            silent: true,
          },
          {
            name: "实际值",
            value: radarData,
            lineStyle: {
              color: "#409eff",
              width: 2,
            },
            areaStyle: {
              color: "rgba(64, 158, 255, 0.12)",
            },
            itemStyle: {
              color: "#409eff",
            },
          },
        ],
      },
    ],
  };
}

export function getYangYinBarOption(yangScore: number, yinScore: number) {
  const formatLabel = (value: number) => value.toFixed(1);

  return {
    grid: {
      left: "80",
      right: "80",
      top: "20",
      bottom: "40",
    },
    xAxis: {
      type: "category",
      data: ["阳气", "阴血"],
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: "rgba(0,0,0,0.85)",
        fontSize: 14,
      },
      axisLine: {
        lineStyle: {
          color: "rgba(0,0,0,0.15)",
        },
      },
    },
    yAxis: {
      type: "value",
      max: YANG_YIN_MAX,
      axisLabel: {
        show: false,
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      splitLine: {
        lineStyle: {
          type: "dashed",
          color: "rgba(0,0,0,0.12)",
        },
      },
    },
    series: [
      {
        type: "bar",
        barWidth: 100,
        data: [
          {
            value: yangScore,
            itemStyle: {
              color: "#f2ebba",
            },
            label: {
              show: true,
              position: "insideTop",
              distance: 12,
              formatter: () => formatLabel(yangScore),
              color: "#fff",
              fontSize: 14,
              backgroundColor: "rgba(196, 176, 96, 0.85)",
              padding: [4, 10],
              borderRadius: 2,
            },
          },
          {
            value: yinScore,
            itemStyle: {
              color: "#1c2d4f",
            },
            label: {
              show: true,
              position: "insideTop",
              distance: 12,
              formatter: () => formatLabel(yinScore),
              color: "#fff",
              fontSize: 14,
              backgroundColor: "rgba(74, 96, 140, 0.85)",
              padding: [4, 10],
              borderRadius: 2,
            },
          },
        ],
      },
    ],
  };
}

export function getYangYinPercent(score: number): number {
  return Math.min(100, Math.max(0, (score / YANG_YIN_MAX) * 100));
}
